import { useMemo, useState } from 'react';
import { TypeCurveStatusTag } from 'components/lifecycle-status-tag';
import classNames from 'classnames';
import { Button, Link } from '@carbon/react';
import { Reset } from '@carbon/react/icons';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import { DcaScenarioSelector } from 'components/well-page-content/decline-curve-analysis/dca-scenario-selector';
import {
  SingleSelectForm,
  type Option,
} from 'components/single select form/single-select-form';
import { Toggle } from 'components/toggle/toggle';
import { TypeCurveVerificationStatusIndicator } from 'components/type-curve-verification-status/type-curve-verification-status';
import type {
  DcaScenariosResponse,
  TypeCurveListingRow,
} from 'types/api-responses';
import type { MineralType } from 'types/minerals';
import { useTcDcaForm } from './tc-dca-form-context';
import { TcDcaFitButton } from './tc-dca-fit-button';
import { TcDcaForecastSettings } from './tc-dca-forecast-settings';
import { TcDcaVolumeModel } from './tc-dca-volume-model';
import { useTypeCurveHeaders } from './use-type-curve-headers';
import type { TcDcaRegressionResponse } from './types';
import styles from './tc-dca-configuration-panel.module.scss';

// ---------------------------------------------------------------------------
// PanelSection — reusable accordion section with per-section reset
// ---------------------------------------------------------------------------

interface PanelSectionProps {
  title: string;
  children: React.ReactNode;
  onReset: () => void;
  defaultExpanded?: boolean;
  disabled?: boolean;
}

const PanelSection = ({
  title,
  children,
  onReset,
  defaultExpanded,
  disabled,
}: PanelSectionProps) => (
  <Accordion defaultExpanded={defaultExpanded}>
    <AccordionSummary className={styles.accordionHeader}>
      <div className={styles.accordionCustomHeader}>
        <span>{title}</span>
        {/* Reset is a <span>, not a form control, so a disabled <fieldset>
            can't neutralize it — gate it here on the read-only page. */}
        <span
          onClick={
            disabled
              ? undefined
              : (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onReset();
                }
          }
          className={classNames(styles.resetIcon, {
            [styles.resetIconDisabled]: disabled,
          })}
        >
          <Reset />
        </span>
      </div>
    </AccordionSummary>
    <AccordionDetails>
      <div className={styles.accordionContent}>{children}</div>
    </AccordionDetails>
  </Accordion>
);

// ---------------------------------------------------------------------------
// Configuration Panel
// ---------------------------------------------------------------------------

interface TcDcaConfigurationPanelProps {
  /** Stream that was selected when the user clicked the pencil icon. */
  stream: MineralType;
  /** Whether the fit mutation is in-flight. */
  isFitting: boolean;
  /** Callback to trigger the fit regression. */
  onFit: () => void;
  /** The most recent regression result (null before first fit). */
  regressionResult:
    TcDcaRegressionResponse['typeCurveDcaRegressionResult'] | null;
  /** Regression error (null when no error). */
  regressionError: Error | null;
  /** Type curve area name used to fetch related type curves. */
  typeCurveAreaName: string;
  /** The currently active type curve ID. */
  activeUniqueTcId: string;
  /** Callback when the user selects a different type curve. */
  onTypeCurveChange: (uniqueTcId: string, headerId: number) => void;
  /** DCA scenario for this modal only — not the page-wide selection. */
  dcaScenario: DcaScenariosResponse | null;
  onDcaScenarioChange: (scenario: DcaScenariosResponse | null) => void;
  /** Read-only on the workspace page — disables resets (inputs/Fit are disabled
   * via the wrapping <fieldset>). */
  readOnly?: boolean;
}

export const TcDcaConfigurationPanel = ({
  stream,
  isFitting,
  onFit,
  regressionResult,
  regressionError,
  typeCurveAreaName,
  activeUniqueTcId,
  onTypeCurveChange,
  dcaScenario,
  onDcaScenarioChange,
  readOnly,
}: TcDcaConfigurationPanelProps) => {
  const { resetStream, resetForecastSettings, resetAll } = useTcDcaForm();

  return (
    <div className={styles.configPanelContainer}>
      {/* Header: title + global reset */}
      <div className={styles.configPanelHeader}>
        <h3 className={styles.configPanelTitle}>Configuration Panel</h3>
        <Button
          size="sm"
          renderIcon={Reset}
          iconDescription="Reset all"
          hasIconOnly
          className={styles.globalResetButton}
          onClick={resetAll}
          disabled={readOnly}
        />
      </div>

      {/* Fit button */}
      <TcDcaFitButton
        stream={stream}
        isLoading={isFitting}
        disabled={!dcaScenario}
        onFit={onFit}
      />

      {/* Regression status messages */}
      {regressionError && (
        <div className={styles.regressionError}>
          Regression failed: {regressionError.message}
        </div>
      )}
      {regressionResult?.status === 'error' &&
        regressionResult.errors.length > 0 && (
          <div className={styles.regressionError}>
            {regressionResult.errors.map((err, i) => (
              <p key={i}>{err.message}</p>
            ))}
          </div>
        )}
      {regressionResult?.status === 'success' && (
        <div className={styles.regressionSuccess}>Regression complete</div>
      )}

      {/* DCA Scenarios */}
      <Accordion>
        <AccordionSummary className={styles.accordionHeader}>
          <span>DCA Scenarios</span>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.accordionContent}>
            <DcaScenarioSelector
              skipFetch
              value={dcaScenario}
              onChange={onDcaScenarioChange}
            />
          </div>
        </AccordionDetails>
      </Accordion>

      {/* Forecast Settings */}
      <PanelSection
        title="Forecast Settings"
        onReset={resetForecastSettings}
        disabled={readOnly}
      >
        <TcDcaForecastSettings />
      </PanelSection>

      {/* OIL Model */}
      <PanelSection
        title="OIL Model"
        onReset={() => resetStream('oil')}
        disabled={readOnly}
      >
        <TcDcaVolumeModel stream="oil" />
      </PanelSection>

      {/* GAS Model */}
      <PanelSection
        title="GAS Model"
        onReset={() => resetStream('gas')}
        disabled={readOnly}
      >
        <TcDcaVolumeModel stream="gas" />
      </PanelSection>

      {/* WATER Model */}
      <PanelSection
        title="WATER Model"
        onReset={() => resetStream('water')}
        disabled={readOnly}
      >
        <TcDcaVolumeModel stream="water" />
      </PanelSection>

      {/* Type Curves */}
      <Accordion>
        <AccordionSummary className={styles.accordionHeader}>
          <span>Type Curves</span>
        </AccordionSummary>
        <AccordionDetails>
          <div className={styles.accordionContent}>
            <TcDcaTypeCurveList
              typeCurveAreaName={typeCurveAreaName}
              activeUniqueTcId={activeUniqueTcId}
              onTypeCurveChange={onTypeCurveChange}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TcDcaTypeCurveList — inline list of type curves for in-modal switching
// ---------------------------------------------------------------------------

interface TcOption extends Option {
  uniqueTcId: string;
  headerId: number;
  href: string;
  formation: string;
  wellSpacingFt: number;
  status: string;
  verificationStatus?: string | null;
  lastVerifiedBy?: string | null;
  promoted?: boolean | null;
  archived?: boolean;
  isDefault?: boolean;
}

interface TcDcaTypeCurveListProps {
  typeCurveAreaName: string;
  activeUniqueTcId: string;
  onTypeCurveChange: (uniqueTcId: string, headerId: number) => void;
}

const toTcOption = (h: TypeCurveListingRow): TcOption => ({
  id: h.uniqueTcId,
  name: h.uniqueTcId,
  uniqueTcId: h.uniqueTcId,
  headerId: h.id,
  href:
    h.resources?.typeCurve?.href ??
    h.resources?.typeCurveHeader?.href ??
    `/type_curves/${h.tcPublicId}`,
  formation: h.formation,
  wellSpacingFt: h.wellSpacingFt,
  status: h.status,
  verificationStatus: h.verification,
  lastVerifiedBy: h.lastVerifiedBy,
  promoted: h.promoted,
  archived: h.archived,
  isDefault: h.isDefault,
});

const TcDcaTypeCurveList = ({
  typeCurveAreaName,
  activeUniqueTcId,
  onTypeCurveChange,
}: TcDcaTypeCurveListProps) => {
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const { data, isPending } = useTypeCurveHeaders(
    typeCurveAreaName,
    showActiveOnly
  );

  const options = useMemo(() => (data?.results ?? []).map(toTcOption), [data]);

  const selectedOption = useMemo(
    () => options.find((o) => o.uniqueTcId === activeUniqueTcId) ?? null,
    [options, activeUniqueTcId]
  );

  return (
    <SingleSelectForm<TcOption>
      id="tc-dca-type-curve-list"
      options={options}
      value={selectedOption}
      onChange={(opt) => {
        if (opt) onTypeCurveChange(opt.uniqueTcId, opt.headerId);
      }}
      renderLabel={(opt) => <TcDcaTypeCurveOptionLabel option={opt} />}
      onSearch={(text, opt) =>
        opt.uniqueTcId.toLowerCase().includes(text.toLowerCase())
      }
      isLoading={isPending}
      header={
        <div className={styles.typeCurveActiveToggleContainer}>
          <Toggle
            isActive={showActiveOnly}
            handleToggle={() => setShowActiveOnly(!showActiveOnly)}
            label="Show Active"
          />
        </div>
      }
    />
  );
};

// ---------------------------------------------------------------------------
// TcDcaTypeCurveOptionLabel — mirrors TypeCurveOption in type-curve-selector
// ---------------------------------------------------------------------------

const TcDcaTypeCurveOptionLabel = ({ option }: { option: TcOption }) => (
  <div className={styles.tcOptionContent}>
    <div className={styles.tcOptionHeader}>
      <TypeCurveVerificationStatusIndicator
        status={option.verificationStatus}
        showLabel={false}
        hoverVerifiedBy
        lastVerifiedBy={option.lastVerifiedBy}
        className={styles.verificationStatusIcon}
        iconClasses={{
          correct: styles.verificationStatusCorrect,
          incorrect: styles.verificationStatusIncorrect,
        }}
      />
      <Link
        className={styles.tcOptionName}
        href={option.href}
        target="_blank"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        {option.uniqueTcId}
      </Link>
      <TypeCurveStatusTag
        promoted={option.promoted}
        isDefault={option.isDefault}
        archived={option.archived}
        className={styles.tcStatusTag}
      />
    </div>
    <div className={styles.tcOptionDetails}>
      {option.formation} | {option.wellSpacingFt} ft | {option.status}
    </div>
  </div>
);
